/**
 * O editor gráfico de um componente do diagrama.
 *
 * **O desenho de UM componente custa 2,5 s; o do diagrama, 10,4 s.** A prévia do
 * diagrama inteiro (7141, 129 componentes) não serve para acompanhar um campo de
 * altura sendo digitado. A rota nova desenha só este componente — e por dentro
 * ela usa o mesmo caminho que a produção usa (carga única do diagrama), porque o
 * caminho "um componente pede as próprias ligações" chegou a 22 s em alguns.
 *
 * **A unidade é o passo**, 1,5 mm. É nela que altura e deslocamento são
 * digitados; o px vem junto só para conferir com o olho o que o traço mostra.
 *
 * **`PUT ligacoes/{id}` é substituição completa**, como o dos terminais: quem não
 * lê `ramificacaoId`, `textoDescricao` e os dois campos da junção grava zero por
 * cima deles. Por isso `GET componentes/{id}/terminais` passou a devolver os
 * quatro — sem eles, mexer numa altura desfazia a junção em silêncio.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto, obter } from "./cliente"

/** A altura de um terminal nas duas unidades em que o desenho pensa. */
export type MedidaDoTerminal = {
  diagramaLigacaoId: number
  pino: string
  posicao: string
  passos: number
  px: number
}

export type DesenhoDoComponente = {
  diagramaComponenteId: number
  diagramaId: number
  titulo: string
  componenteCodigo: string
  componenteDescricao: string
  posicao: string
  /** Documento SVG completo — vai direto num `srcDoc`/`<img>` ou embutido. */
  svg: string
  passoPx: number
  passoMm: number
  larguraPassos: number
  larguraPx: number
  alturaPassos: number
  alturaPx: number
  /**
   * O tamanho da TELA do desenho, que não é o do componente: o motor desenha
   * numa faixa de 68 passos (385 px) porque ali cabem o componente, a ligação e
   * a coluna de texto. É esta a largura que o visor usa para escalar.
   */
  svgLarguraPx: number
  svgAlturaPx: number
  terminais: MedidaDoTerminal[]
  milissegundosBusca: number
  milissegundosDesenho: number
  milissegundosEstilo: number
}

/**
 * `staleTime: 0` de propósito: o traço é o retrato de agora, e depois de gravar
 * uma altura o que se quer ver é o desenho novo, não o de dez segundos atrás.
 */
export function usarDesenhoDoComponente(diagramaComponenteId: number, grid = true) {
  return useQuery({
    queryKey: ["componente-desenho", diagramaComponenteId, grid] as const,
    enabled: diagramaComponenteId > 0,
    staleTime: 0,
    queryFn: () => obter<DesenhoDoComponente>(
      `api/diagramas/componentes/${diagramaComponenteId}/desenho`, { grid }),
  })
}

/** Um terminal que este pode tomar como ramificação ou como junção. */
export type CandidatoDeLigacao = {
  diagramaLigacaoId: number
  diagramaComponenteId: number
  pinoutId: number
  componenteCodigo: string
  componenteDescricao: string
  pino: string
  circuitoDescricao: string
  pinoDaLigacao: string
}

/**
 * A mesma lista serve aos dois campos — no legado são dois botões, "Carregar
 * ramificação" e "Carregar junção", que chamam a mesma consulta. Só carrega
 * quando o campo pede, que é o que o legado faz: são todos os terminais do
 * diagrama no mesmo circuito, e num diagrama grande isso não é lista de abrir à
 * toa.
 */
export function usarCandidatosDaLigacao(diagramaLigacaoId: number, ligado: boolean) {
  return useQuery({
    queryKey: ["ligacao-candidatos", diagramaLigacaoId] as const,
    enabled: ligado && diagramaLigacaoId > 0,
    staleTime: 30_000,
    queryFn: () => obter<CandidatoDeLigacao[]>(
      `api/diagramas/ligacoes/${diagramaLigacaoId}/candidatos`, {}),
  })
}

/** O corpo inteiro do `DiagramaLigacaoFinalizar`. */
export type LigacaoParaGravar = {
  altura: number
  deslocar: boolean
  ramificacao: number
  ramificacaoId: number
  ramificacaoPosicao: number
  ocultar: boolean
  juncao: boolean
  juncaoDiagramaComponenteId: number
  juncaoPinoutId: number
  texto: string
  textoDescricao: string
}

/**
 * Grava a ligação e derruba o desenho do componente e o dos vizinhos que a
 * resposta apontar — uma junção mexe nos dois lados, e mostrar só um deles
 * atualizado seria pior do que não mostrar nenhum.
 */
export function usarEditarLigacao(projetoId: number, diagramaId: number, componenteId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, corpo }: { id: number; corpo: LigacaoParaGravar }) => {
      const r = await api.put<{ ok: boolean; componentesParaAtualizar: number[] }>(
        `api/diagramas/ligacoes/${id}`, corpo, noProjeto(projetoId))
      return r.data
    },
    onSuccess: (r) => {
      for (const outro of [componenteId, ...(r?.componentesParaAtualizar ?? [])])
        qc.invalidateQueries({ queryKey: ["componente-desenho", outro] })
      qc.invalidateQueries({ queryKey: ["componente-terminais", componenteId] })
      qc.invalidateQueries({ queryKey: ["diagrama-componentes", diagramaId] })
      qc.invalidateQueries({ queryKey: ["diagrama-desenho-estado", diagramaId] })
      qc.invalidateQueries({ queryKey: ["diagramas", projetoId] })
    },
  })
}

/** `ramificacao` — de onde o traço sai. Os nomes são os do select do legado. */
export const RAMIFICACOES: ReadonlyArray<readonly [number, string]> = [
  [0, "Não ramifica"],
  [1, "A partir do componente principal"],
  [2, "A partir do componente secundário"],
]
